// Ported verbatim from nenda-autoplayer's src/lib/state/guards.ts. Pure
// functions, zero framework imports - only the import paths changed
// (`AutoplayerMachineContext` from the extracted ./context.ts instead of
// ./autoplayerMachine, avoiding a circular import; `ScheduleType` from the
// ported ../types/Schedule; `getCurrentContentPlaylistId` from the ported
// ./scheduleUtils, see that file's own header).
//
// `areAdsAllowed`/`isSignageAllowed` are THE gate that makes `getAdSlot`/
// `registerAdView` confirmed-unreachable for Foyer (see
// src/tv/signage/foyerScheduleClient.ts's own doc comment) - `areAdsAllowed`
// reads `adPermissions.external.fullTakeoverAllowed`, which
// legacySignageSchedule.ts's `NO_AD_PERMISSIONS` hardcodes `false` on every
// entry. `isSignageAllowed` reads the `.signage` side of the same object,
// which legacySignageSchedule.ts computes from `!isChannelPlaylistId(...)`
// (the campaign-break landmine fixed during the target-architecture design,
// see the plan) - THIS is the guard that would have silently killed every
// campaign break had that fix not landed first.
import { ScheduleType } from "../types/Schedule";
import { getCurrentContentPlaylistId } from "./scheduleUtils";
import { AutoplayerMachineContext } from "./context";

export const hasNextAsset = (
  context: AutoplayerMachineContext,
  type: ScheduleType
): boolean => {
  switch (type) {
    case ScheduleType.Content:
      return (
        context.playback.content.currentContent?.items !== undefined &&
        context.playback.content.currentIndex <
          context.playback.content.currentContent?.items.length - 1
      );
    case ScheduleType.Ad:
      return (
        context.playback.ad.adslot !== undefined &&
        context.playback.ad.currentIndex <
          context.playback.ad.adslot?.ads.length - 1
      );
    case ScheduleType.Signage:
      return (
        context.playback.signage.currentIndex !== undefined &&
        context.playback.signage.currentIndex <
          context.playback.signage.currentSignagePlaylist.length - 1
      );
    default:
      return false;
  }
};

export const areAdsAllowed = (context: AutoplayerMachineContext): boolean => {
  const currentContentPlaylist = context.playback.content.currentContent;
  if (!currentContentPlaylist) return true;

  const adPermissions = currentContentPlaylist.adPermissions;

  return adPermissions.external.fullTakeoverAllowed;
};

export const isSignageAllowed = (
  context: AutoplayerMachineContext
): boolean => {
  const currentContentPlaylist = context.playback.content.currentContent;
  if (!currentContentPlaylist) return true;

  const adPermissions = currentContentPlaylist.adPermissions;

  return adPermissions.signage.fullTakeoverAllowed;
};

export const shouldPlayAd = (context: AutoplayerMachineContext) => {
  return (
    context.playback?.activeSlot != undefined &&
    context.playback.activeSlot.type === ScheduleType.Ad
  );
};

export const shouldPlaySignage = (context: AutoplayerMachineContext) => {
  if (shouldPlayAd(context)) {
    return false;
  }
  return (
    context.playback?.activeSlot != undefined &&
    context.playback.activeSlot.type === ScheduleType.Signage
  );
};

export const hasContentChanged = (context: AutoplayerMachineContext) => {
  if (!context.schedule?.contentSchedule) return false;

  const conentPlaylistId = getCurrentContentPlaylistId(context.schedule);

  if (!conentPlaylistId) return false;

  return context.playback.content?.currentPlaylistId !== conentPlaylistId;
};

export const hasAds = (context: AutoplayerMachineContext) => {
  return (
    context.playback?.ad != undefined &&
    context.playback.ad.adslot != undefined &&
    context.playback.ad.adslot?.numberOfAds > 0
  );
};

export const hasSignage = (context: AutoplayerMachineContext) => {
  return (
    context.playback?.signage != undefined &&
    context.playback.signage.currentSignagePlaylist.length > 0
  );
};
